import {
  getDefaultZIndex,
  Avatar as MantineAvatar,
  Badge as MantineBadge,
  ColorSwatch as MantineColorSwatch,
  Indicator as MantineIndicator,
  Loader as MantineLoader,
  Skeleton as MantineSkeleton,
  ThemeIcon as MantineThemeIcon,
} from "@mantine/core";
import classNames from "classnames";
import * as React from "react";
import classes from "./indicators.module.css";
import type { ElementProps, SizeValue, StyleProps, ThemeColor } from "./style-props";

/**
 * `theme.defaultRadius`, which Mantine 6 used for an absent Avatar `radius`. Mantine 7 rounds an
 * Avatar fully by default and exposes the theme value as this variable (SC-2703).
 */
const THEME_DEFAULT_RADIUS = "var(--mantine-radius-default)";

export interface BadgeProps extends ElementProps<"div">, StyleProps {
  color?: string;
  variant?: "light" | "filled" | "outline" | "dot" | "gradient";
  /** Non-numeric: Mantine resolves Badge size by theme key only, so a number would silently produce no size. */
  size?: Exclude<SizeValue, number>;
  radius?: SizeValue;
  fullWidth?: boolean;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  /**
   * The root element. Open for the same reason `PaperProps.component` is: the one non-div call site
   * (`FileUploadStatus`) renders a custom root, and `icon` below exists for it — Mantine forwards
   * unrecognized props to the root, which is how that root receives its icon.
   */
  component?: React.ElementType;
  icon?: React.ReactNode;
}

/**
 * Same flattening as `PaperRoot` in the surfaces module: Mantine types `Badge` as a polymorphic
 * union whose `component` arm selects the element prop set, and a non-generic wrapper cannot
 * restate that. `indicators-casts.test.ts` pins what it can: that the named prop *keys* remain
 * keys of Mantine's own BadgeProps. It cannot pin `component` (supplied by the polymorphic
 * factory, not the props type) or `icon` (a passthrough that reaches the custom root through
 * Mantine's rest-prop spread) — those rest on the cast alone.
 */
const BadgeRoot = MantineBadge as React.ComponentType<BadgeProps & { readonly ref?: React.Ref<HTMLDivElement> }>;

/**
 * Mantine 6 defaulted Badge to the `light` variant and `xl` radius; Mantine 7 defaults to `filled`
 * and a fully rounded radius. The wrapper keeps the Mantine 6 defaults (SC-2703).
 */
export const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(function Badge(
  { children, className, variant = "light", radius = "xl", ...props },
  ref,
) {
  return (
    <BadgeRoot {...props} className={classNames(classes.badgeWidth, className)} variant={variant} radius={radius} ref={ref}>
      {children}
    </BadgeRoot>
  );
});

/**
 * Loader renders Mantine's default `oval` loader — an `<svg>` in Mantine 6, a CSS-drawn `<span>` in
 * Mantine 7, at the same size scale (SC-2703). No call site passes element props (only `size`,
 * `color`, and margin style props), so the element surface is not declared until a consumer needs
 * it — the same rule that keeps `Card.Section` unexported.
 */
export interface LoaderProps extends StyleProps {
  size?: SizeValue;
  color?: string;
  /** One call site (`resolved-document-money-amount`) inlines display/vertical-align on the svg. */
  style?: React.CSSProperties;
}

export const Loader = (props: LoaderProps) => <MantineLoader {...props} />;

export interface ThemeIconProps extends ElementProps<"div">, StyleProps {
  children: React.ReactNode;
  size?: SizeValue;
  radius?: SizeValue;
  color?: string;
  variant?: "filled" | "light" | "outline" | "default" | "gradient";
}

/** Mantine 6's ThemeIcon width and height per size key, in px; Mantine 7's scale is 2–4px larger. */
const THEME_ICON_SIZES: Partial<Record<string, number>> = { xs: 16, sm: 20, md: 26, lg: 32, xl: 40 };

/**
 * A size key keeps its Mantine 6 dimension (SC-2703): the wrapper resolves it to that px value,
 * which Mantine 7 converts to rem. Numbers and raw CSS lengths pass through unchanged.
 */
export const ThemeIcon = React.forwardRef<HTMLDivElement, ThemeIconProps>(function ThemeIcon({ children, size = "md", ...props }, ref) {
  return (
    <MantineThemeIcon {...props} size={typeof size === "string" ? (THEME_ICON_SIZES[size] ?? size) : size} ref={ref}>
      {children}
    </MantineThemeIcon>
  );
});

// `Avatar.Group` has zero call sites outside `src/ui`, so it is deliberately not exported.
export interface AvatarProps extends ElementProps<"div">, StyleProps {
  src?: string | null;
  /** Alt text for the image, or the title of the placeholder when `src` is absent. */
  alt?: string;
  size?: SizeValue;
  radius?: SizeValue;
  color?: string;
  /** Custom placeholder (initials, an icon) shown when `src` is absent or fails to load. */
  children?: React.ReactNode;
}

/**
 * Same polymorphic flattening as `BadgeRoot` above; `component` is not declared because no call
 * site renders Avatar as anything but its default root.
 */
const AvatarRoot = MantineAvatar as React.ComponentType<AvatarProps & { readonly ref?: React.Ref<HTMLDivElement> }>;

/**
 * Mantine 6's Avatar took the theme default radius; Mantine 7's is a circle. The wrapper keeps Mantine 6's.
 * Mantine 6 also defaulted the color to `gray` (variant `light`), so a placeholder always drew that color's
 * light fill; Mantine 7 applies variant colors only when a color or variant is passed and otherwise falls
 * back to its own translucent gray. Restating `gray` gives the theme resolver its Mantine 6 light fill and
 * text (SC-2703); Mantine 7 already defaults the variant to `light`.
 */
export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(function Avatar(
  { children, radius = THEME_DEFAULT_RADIUS, color = "gray", ...props },
  ref,
) {
  return (
    <AvatarRoot {...props} radius={radius} color={color} ref={ref}>
      {children}
    </AvatarRoot>
  );
});

export interface SkeletonProps extends ElementProps<"div">, StyleProps {
  height?: number | string;
  width?: number | string;
  radius?: SizeValue;
  /** Width and border-radius equal to `height`. */
  circle?: boolean;
  /** When false, renders children without the skeleton overlay. */
  visible?: boolean;
  children?: React.ReactNode;
}

export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton({ children, ...props }, ref) {
  return (
    <MantineSkeleton {...props} ref={ref}>
      {children}
    </MantineSkeleton>
  );
});

/**
 * The single call site (`ColorSelect`) passes only these three props; element props, `children`,
 * and style props are not declared until a consumer needs them — the same rule that keeps
 * `Loader`'s surface minimal.
 */
export interface ColorSwatchProps {
  /** Swatch background, any valid CSS color. */
  color: string;
  /** Width and height. */
  size?: number | string;
  radius?: SizeValue;
}

/** Same polymorphic flattening as `BadgeRoot` above; `ColorSwatch` renders only its default `<div>` root. */
const ColorSwatchRoot = MantineColorSwatch as React.ComponentType<ColorSwatchProps & { readonly ref?: React.Ref<HTMLDivElement> }>;

/** Mantine 6 sized and rounded the swatch at 25px by default; Mantine 7 uses 28px and a full circle. */
export const ColorSwatch = React.forwardRef<HTMLDivElement, ColorSwatchProps>(function ColorSwatch(
  { size = 25, radius = 25, ...props },
  ref,
) {
  return <ColorSwatchRoot {...props} size={size} radius={radius} ref={ref} />;
});

export type IndicatorPosition =
  | "top-start"
  | "top-center"
  | "top-end"
  | "middle-start"
  | "middle-center"
  | "middle-end"
  | "bottom-start"
  | "bottom-center"
  | "bottom-end";

export interface IndicatorProps extends ElementProps<"div">, StyleProps {
  children: React.ReactNode;
  position?: IndicatorPosition;
  offset?: number;
  inline?: boolean;
  size?: number | string;
  label?: React.ReactNode;
  radius?: SizeValue;
  color?: ThemeColor;
  withBorder?: boolean;
  disabled?: boolean;
  processing?: boolean;
  zIndex?: React.CSSProperties["zIndex"];
}

/** Mantine 6 stacked the indicator at the `app` z-index (100); Mantine 7 defaults it to 200. The wrapper keeps 100. */
export const Indicator = React.forwardRef<HTMLDivElement, IndicatorProps>(function Indicator(
  { children, zIndex = getDefaultZIndex("app"), ...props },
  ref,
) {
  return (
    <MantineIndicator {...props} zIndex={zIndex} ref={ref}>
      {children}
    </MantineIndicator>
  );
});
